import { liveFeedbackPositionStateAtom } from '@realtime';
import { atom, useAtomValue } from 'jotai';

import { ParameterNode } from '@data';
import { useTranslate } from '@i18n';
import { uiNodeBySmpIdAtom } from '@store';
import { Box, Text, u } from '@ui';

import { LABEL_TYPE, ParameterRegistration } from './ParameterRegistration';
import * as css from './styles.css';

export const PARAMETER_CAP = 7;

const SETUP_POSITION = 'P1';

// We currently do not have time series i18n, so we trim p position from parameter name.
const positionRegex = /^P(?:[1-9]|10) /gm;

const liveAnalysisParameterRegistrationGroups = atom((get) => {
    const position = get(liveFeedbackPositionStateAtom);
    const parametersMap = get(uiNodeBySmpIdAtom);
    const positionParameters = Object.entries(position?.parameters ?? {})
        .slice(0, PARAMETER_CAP)
        .reduce(
            (acc, [id, values]) => {
                const parameterNode = parametersMap[Number(id)];
                const timeSeriesId = parameterNode?.parameter?.timeSeriesId;
                const axis = parameterNode?.parameter?.axis;
                if (!timeSeriesId) return acc;
                const groupId = `${timeSeriesId}:${axis}`;
                if (!acc[groupId]) acc[groupId] = [];
                acc[groupId].push([parameterNode, values]);
                return acc;
            },
            {} as Record<string, [ParameterNode, number[]][]>,
        );
    return Object.values(positionParameters);
});

export const ParameterRegistrationList = () => {
    const positionState = useAtomValue(liveFeedbackPositionStateAtom);
    const isSetup = !positionState || positionState?.position.toUpperCase() === SETUP_POSITION;

    const parameterRegistrationGroups = useAtomValue(liveAnalysisParameterRegistrationGroups);

    const positionMap = positionState?.parameters ?? {};
    const positionEntries = Object.entries(positionMap).slice(0, PARAMETER_CAP);
    const t = useTranslate();

    if (isSetup) {
        return (
            <Box display="flex" flexDirection="column" alignSelf="stretch" alignItems={'stretch'} gap={u(2)}>
                <Box p={u(1)} display="flex" justifyContent="center" alignItems="center" className={css.positionBox}>
                    <Text variant="h1">{positionState?.position.toUpperCase() ?? SETUP_POSITION}</Text>
                </Box>
                <Box display="flex" flexDirection="column" gap={u(2)} height="100%">
                    {positionEntries.map(([id, values]) =>
                        values.length > 0 ? (
                            <ParameterRegistration
                                key={id}
                                id={Number(id)}
                                values={values}
                                labelType={LABEL_TYPE.PARAMETER}
                            />
                        ) : null,
                    )}
                </Box>
            </Box>
        );
    }

    return (
        <Box display="flex" flexDirection="column" alignSelf="stretch" alignItems="stretch" gap={u(2)}>
            {parameterRegistrationGroups.map((parameterList) => {
                return (
                    <Box display="flex" flexDirection="column" alignSelf="stretch" alignItems="stretch" gap={u(2)}>
                        <Box
                            p={u(1)}
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            className={css.positionBox}
                        >
                            <Text variant="h3">
                                {t(parameterList.at(0)?.[0].i18n ?? '-').replace(positionRegex, '')}
                            </Text>
                        </Box>
                        <Box display="flex" flexDirection="column" gap={u(2)}>
                            {parameterList?.map(([node, values]) => (
                                <ParameterRegistration
                                    key={node.id}
                                    id={Number(node.parameter.smpId)}
                                    values={values}
                                    labelType={LABEL_TYPE.POSITION}
                                />
                            ))}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};
